import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Edit,
  ExternalLink,
  Eye,
  Loader2,
  Mail,
  MapPin,
  MoreVertical,
  Pencil,
  Phone,
  Plus,
  Search,
  Store,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/use-debounce";
import countries from "@/constants/array/country_city.json";
import { apiErrorMessage } from "@/api/perkpoint/client";
import { OutletsApi, outletKeys, type OutletInput } from "@/api/perkpoint/outlets";
import { useActiveProgramId } from "./programs/program-scope";

interface Outlet {
  id: string;
  outletName: string;
  address: string;
  city: string;
  country: string;
  state: string;
  postalCode: string;
  phone: string;
  email: string;
}

// Form schema
const outletFormSchema = z.object({
  outletName: z.string().min(1, { message: "Outlet name is required" }),
  address: z.string().min(1, { message: "Address is required" }),
  city: z.string().min(1, { message: "City is required" }),
  country: z.string().min(1, { message: "Country is required" }),
  state: z.string().min(1, { message: "State is required" }),
  postalCode: z.string().min(1, { message: "Postal code is required" }),
  phone: z.string().min(1, { message: "Phone number is required" }),
  email: z.string().email({ message: "Invalid email address" }),
});

type OutletFormValues = z.infer<typeof outletFormSchema>;

const fullAddress = (outlet: Outlet) =>
  [outlet.address, outlet.city, outlet.state, outlet.postalCode, outlet.country]
    .filter(Boolean)
    .join(", ");

function toInput(data: OutletFormValues, countryName: string): OutletInput {
  return {
    name: data.outletName,
    address: data.address,
    city: data.city,
    state: data.state,
    country: countryName,
    postal_code: data.postalCode,
    phone: data.phone,
    email: data.email,
  };
}

export default function Outlets() {
  const { programId, isLoading: programLoading } = useActiveProgramId();
  const queryClient = useQueryClient();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState<Outlet | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [outletToDelete, setOutletToDelete] = useState<Outlet | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const { toast } = useToast();

  const copyToClipboard = async (field: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      toast({
        title: "Copy failed",
        description: "Your browser blocked clipboard access.",
        variant: "destructive",
      });
    }
  };
  const pageSize = 10;
  const debouncedSearch = useDebounce(search, 300);
  const outletsQuery = useQuery({
    queryKey: outletKeys.list(programId ?? "", { search: debouncedSearch, page, page_size: pageSize }),
    queryFn: () => OutletsApi.list(programId!, { search: debouncedSearch || undefined, page, page_size: pageSize }),
    enabled: Boolean(programId),
  });
  const outlets: Outlet[] = (outletsQuery.data?.items ?? []).map((outlet) => ({
    id: outlet.id,
    outletName: outlet.name,
    address: outlet.address,
    city: outlet.city,
    country: outlet.country,
    state: outlet.state,
    postalCode: outlet.postal_code,
    phone: outlet.phone,
    email: outlet.email,
  }));
  const isLoading = programLoading || outletsQuery.isLoading;

  const form = useForm<OutletFormValues>({
    resolver: zodResolver(outletFormSchema),
    defaultValues: {
      outletName: "",
      address: "",
      city: "",
      country: "",
      state: "",
      postalCode: "",
      phone: "",
      email: "",
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: outletKeys.all });

  const saveOutlet = useMutation({
    mutationFn: (input: OutletInput) =>
      selectedOutlet
        ? OutletsApi.update(programId!, selectedOutlet.id, input)
        : OutletsApi.create(programId!, input),
    onSuccess: () => refresh(),
  });

  const onSubmit = async (data: OutletFormValues) => {
    if (!programId) return;
    const countryName = selectedCountry?.name || data.country;
    try {
      const editing = Boolean(selectedOutlet);
      await saveOutlet.mutateAsync(toInput(data, countryName));
      toast({
        title: "Success",
        description: editing ? "Outlet updated successfully" : "Outlet added successfully",
      });
      form.reset();
      setSelectedCountry(null);
      setSelectedOutlet(null);
      setIsAddModalOpen(false);
      setIsEditModalOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: apiErrorMessage(error, "Failed to save outlet"),
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    form.reset();
    setSelectedCountry(null);
    setSelectedOutlet(null);
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
  };

  const handleView = (outlet: Outlet) => {
    setSelectedOutlet(outlet);
    setIsViewModalOpen(true);
  };

  const handleEdit = (outlet: Outlet) => {
    setSelectedOutlet(outlet);
    // Find country object from countries list
    const countryObj = (countries as any)?.find(
      (c: any) => c.name === outlet.country
    );
    
    // Pre-fill form with outlet data
    form.reset({
      outletName: outlet.outletName,
      address: outlet.address,
      city: outlet.city,
      country: countryObj ? JSON.stringify(countryObj) : outlet.country,
      state: outlet.state,
      postalCode: outlet.postalCode,
      phone: outlet.phone,
      email: outlet.email,
    });
    
    setSelectedCountry(countryObj || null);
    setIsViewModalOpen(false);
    setIsEditModalOpen(true);
  };

  const deleteOutlet = useMutation({
    mutationFn: (outlet: Outlet) => OutletsApi.remove(programId!, outlet.id),
    onSuccess: () => refresh(),
  });

  const confirmDelete = async () => {
    if (!outletToDelete || !programId) return;
    try {
      await deleteOutlet.mutateAsync(outletToDelete);
      toast({
        title: "Outlet deleted",
        description: `${outletToDelete.outletName} was removed from this program.`,
      });
      setOutletToDelete(null);
      setIsViewModalOpen(false);
      if (selectedOutlet?.id === outletToDelete.id) setSelectedOutlet(null);
    } catch (error) {
      toast({
        title: "Error",
        description: apiErrorMessage(error, "Failed to delete outlet"),
        variant: "destructive",
      });
    }
  };

  const total = outletsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = outlets;
  const rangeStart = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, total);
  const isDeleting = deleteOutlet.isPending;

  if (!programLoading && !programId) {
    return (
      <div className="mx-auto max-w-xl py-16 text-center">
        <Store className="mx-auto h-8 w-8 text-slate-400" />
        <h1 className="mt-3 text-xl font-semibold text-slate-900">No program selected</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Outlets belong to one program. Open a program as manager to manage its locations.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-2">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Outlets
          </h1>
          <p className="mt-1 text-muted-foreground">
            Store locations for this program. Search, add, and edit outlets here.
          </p>
        </div>
        <Button
          className="bg-green-600 hover:bg-green-700"
          onClick={() => {
            form.reset();
            setSelectedOutlet(null);
            setSelectedCountry(null);
            setIsAddModalOpen(true);
          }}
        >
          <Plus className="mr-1.5 h-4 w-4" />
          Add outlet
        </Button>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="space-y-4 p-4 sm:p-5">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Search by name, city, phone, or email"
              className="h-10 bg-white pl-9"
            />
          </div>

          <div className="w-full overflow-x-auto rounded-lg border border-slate-200">
            <Table className="min-w-[980px]">
              <TableHeader>
                <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                  <TableHead className="min-w-[240px]">Outlet</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Postal code</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      Loading outlets...
                    </TableCell>
                  </TableRow>
                ) : pageRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      {search.trim()
                        ? "No outlets match your search."
                        : "No outlets yet."}
                    </TableCell>
                  </TableRow>
                ) : (
                  pageRows.map((outlet) => (
                    <TableRow key={outlet.id}>
                      <TableCell className="min-w-[240px]">
                        <p className="font-semibold text-slate-900">
                          {outlet.outletName || "-"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {outlet.address || "-"}
                        </p>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-slate-700">
                        {outlet.city || "-"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-slate-700">
                        {outlet.state || "-"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-slate-700">
                        {outlet.country || "-"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-slate-700">
                        {outlet.postalCode || "-"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-slate-700">
                        {outlet.phone || "-"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-slate-700">
                        {outlet.email || "-"}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              aria-label={`Actions for ${outlet.outletName}`}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleView(outlet)}>
                              <Eye className="mr-2 h-4 w-4" />
                              View details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEdit(outlet)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600 focus:text-red-600"
                              onClick={() => setOutletToDelete(outlet)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {rangeStart}-{rangeEnd} of {total} outlets
            </p>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={currentPage <= 1}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="icon"
                className="h-8 w-8 bg-green-600 text-white hover:bg-green-700"
              >
                {currentPage}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={currentPage >= totalPages}
                onClick={() =>
                  setPage((value) => Math.min(totalPages, value + 1))
                }
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog
        open={outletToDelete !== null}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setOutletToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete outlet</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {outletToDelete?.outletName || "this outlet"} from this program?
              Past transactions keep this outlet name.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
              onClick={() => void confirmDelete()}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Outlet Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-h-[90vh] gap-0 overflow-y-auto p-0 sm:max-w-xl">
          {selectedOutlet && (
            <>
              <DialogHeader className="space-y-0 border-b border-slate-200 p-6 pr-12 text-left">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-700 ring-1 ring-green-100">
                    <Store className="h-6 w-6" />
                  </div>
                  <div className="min-w-0">
                    <DialogTitle className="truncate text-lg font-semibold text-slate-900">
                      {selectedOutlet.outletName || "Unnamed outlet"}
                    </DialogTitle>
                    <DialogDescription className="mt-1 flex items-center gap-1.5 text-sm">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">
                        {[selectedOutlet.city, selectedOutlet.country]
                          .filter(Boolean)
                          .join(", ") || "Location not set"}
                      </span>
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 p-6">
                <section className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Location
                    </h3>
                    {fullAddress(selectedOutlet) && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                          fullAddress(selectedOutlet),
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-medium text-green-700 hover:text-green-800 hover:underline"
                      >
                        Open in Maps
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                    <p className="text-sm font-medium text-slate-900">
                      {selectedOutlet.address || "-"}
                    </p>
                    <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
                      {[
                        ["City", selectedOutlet.city],
                        ["State", selectedOutlet.state],
                        ["Country", selectedOutlet.country],
                        ["Postal code", selectedOutlet.postalCode],
                      ].map(([label, value]) => (
                        <div key={label} className="min-w-0">
                          <dt className="text-xs text-slate-500">{label}</dt>
                          <dd className="mt-0.5 truncate text-sm text-slate-900">
                            {value || "-"}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </section>

                <section className="space-y-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Contact
                  </h3>
                  <div className="divide-y divide-slate-200 rounded-lg border border-slate-200">
                    {[
                      {
                        key: "phone",
                        label: "Phone",
                        icon: Phone,
                        value: selectedOutlet.phone,
                        href: `tel:${selectedOutlet.phone}`,
                      },
                      {
                        key: "email",
                        label: "Email",
                        icon: Mail,
                        value: selectedOutlet.email,
                        href: `mailto:${selectedOutlet.email}`,
                      },
                    ].map(({ key, label, icon: Icon, value, href }) => (
                      <div key={key} className="flex items-center gap-3 p-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-slate-500">{label}</p>
                          {value ? (
                            <a
                              href={href}
                              className="block truncate text-sm font-medium text-slate-900 hover:text-green-700 hover:underline"
                            >
                              {value}
                            </a>
                          ) : (
                            <p className="text-sm text-slate-400">Not provided</p>
                          )}
                        </div>
                        {value && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 text-slate-500"
                            aria-label={`Copy ${label.toLowerCase()}`}
                            onClick={() => void copyToClipboard(key, value)}
                          >
                            {copiedField === key ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              </div>

              <DialogFooter className="flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/60 p-4 sm:flex-row sm:items-center sm:justify-between sm:space-x-0">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => setOutletToDelete(selectedOutlet)}
                >
                  <Trash2 className="mr-1.5 h-4 w-4" />
                  Delete
                </Button>
                <div className="flex flex-col-reverse gap-2 sm:flex-row">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsViewModalOpen(false)}
                  >
                    Close
                  </Button>
                  <Button
                    type="button"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleEdit(selectedOutlet)}
                  >
                    <Edit className="mr-1.5 h-4 w-4" />
                    Edit outlet
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add / Edit Outlet Modal */}
      <Dialog
        open={isAddModalOpen || isEditModalOpen}
        onOpenChange={(open) => {
          if (!open && !form.formState.isSubmitting) handleCancel();
        }}
      >
        <DialogContent className="max-h-[90vh] gap-0 overflow-y-auto p-0 sm:max-w-2xl">
          <DialogHeader className="space-y-0 border-b border-slate-200 p-6 pr-12 text-left">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-700 ring-1 ring-green-100">
                {isEditModalOpen ? (
                  <Pencil className="h-5 w-5" />
                ) : (
                  <Store className="h-6 w-6" />
                )}
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-lg font-semibold text-slate-900">
                  {isEditModalOpen ? "Edit outlet" : "Add new outlet"}
                </DialogTitle>
                <DialogDescription className="mt-1 text-sm">
                  {isEditModalOpen
                    ? `Update the details for ${selectedOutlet?.outletName || "this outlet"}.`
                    : "Add a store location so members can earn and redeem there."}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
              <div className="space-y-6 p-6">
                <section className="space-y-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Outlet
                  </h3>
                  <FormField
                    control={form.control}
                    name="outletName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Outlet name <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Store className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <Input
                              placeholder="e.g. Downtown Flagship Store"
                              className="pl-9"
                              autoFocus
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </section>

                <section className="space-y-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Location
                  </h3>
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Street address <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <Input
                              placeholder="Building, street, area"
                              className="pl-9"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="country"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Country <span className="text-red-500">*</span>
                          </FormLabel>
                          <Select
                            onValueChange={(value) => {
                              const selected = JSON.parse(value);
                              setSelectedCountry(selected);
                              field.onChange(value);
                            }}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select country" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {(countries as any)?.map((country: any) => (
                                <SelectItem
                                  key={country?.name}
                                  value={JSON.stringify(country)}
                                >
                                  {country?.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            State / Province <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Enter state" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            City <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Enter city" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="postalCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Postal code <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Enter postal code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </section>

                <section className="space-y-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Contact
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Phone <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                              <Input
                                type="tel"
                                placeholder={
                                  selectedCountry?.phone_code
                                    ? `+${String(selectedCountry.phone_code).replace(/^\+/, "")} ...`
                                    : "Enter phone number"
                                }
                                className="pl-9"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Email <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                              <Input
                                type="email"
                                placeholder="outlet@example.com"
                                className="pl-9"
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </section>
              </div>

              <DialogFooter className="flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/60 p-4 sm:flex-row sm:items-center sm:justify-between sm:space-x-0">
                <p className="text-xs text-muted-foreground">
                  <span className="text-red-500">*</span> Required fields
                </p>
                <div className="flex flex-col-reverse gap-2 sm:flex-row">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={form.formState.isSubmitting}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-green-600 hover:bg-green-700"
                    disabled={form.formState.isSubmitting}
                  >
                    {form.formState.isSubmitting ? (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    ) : isEditModalOpen ? (
                      <Check className="mr-1.5 h-4 w-4" />
                    ) : (
                      <Plus className="mr-1.5 h-4 w-4" />
                    )}
                    {form.formState.isSubmitting
                      ? "Saving..."
                      : isEditModalOpen
                        ? "Save changes"
                        : "Add outlet"}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
